import { forwardRef, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ComponentUtils from 'utils/component.util';
import ObjectUtils from 'utils/object.util';
import useChat from 'hooks/chat/useChat.hook';
import ConversationInfo from 'views/chat/components/items/ConversationInfo.component';
import Conversation from '../items/Conversation.component';
import ConversationContactInfo from 'views/chat/components/items/ConversationContactInfo.component';
import useComponent from 'hooks/useComponent.hook';

/**
 * @typedef {Object} RefType
 * @property {Object} current
 */

/**
 * @typedef {Object} Props
 * @property {RefType} ref
 * @property {Object} render Define if render elements
 * @property {Object} line Line
 * @property {Object} conversation Conversation to chat
 */

const Component = {
  name: 'ChatSection',

  /**
   * @type {React.FC<Props>}
   * @type {RefType}
   */
  element: forwardRef((props, ref) => {
    const chat = useChat();
    const component = useComponent({
      state: {
        element: {
          conversationContactInfo: {
            open: {
              show: false
            }
          }
        }
      }
    })

    const element = {
      ref: useRef(ref),
      conversation: {
        ref: useRef(null),
      },
      conversationContactInfo: {
        open: {
          onClick: () => {
            component.setState((state) => {
              state.element.conversationContactInfo.open.show = !state.element.conversationContactInfo.open.show;
            })
          }
        }
      }
    }

    const render = useMemo(() => {
      const element = {
        conversationInfo: {
          isRender: false,
          actions: {
            reassign: {
              isRender: false,
            },
            finalize: {
              isRender: false,
            },
            reopen: {
              isRender: false,
            },
            close: {
              isRender: false,
            }
          }
        },
        conversation: {
          isRender: false,
          loading: {
            isRender: false
          },
          content: {
            isRender: false
          },
          addMessage: {
            isRender: false
          },
          quicklyReply: {
            isRender: false
          },
          attachmentFile: {
            isRender: false
          },
          notice: {
            isRender: false
          },
        },
        conversationContactInfo: {
          isRender: false,
          open: {
            isRender: false
          },
          data: {
            isRender: false
          },
          actions: {
            isRender: false
          }
        }
      };
      if (chat.selected.conversation) {
        ObjectUtils.setPathFromObject(element, 'conversationInfo.isRender', props.render, true);
        if (!chat.selected.conversation.isFinished && !chat.selected.conversation.isReassigned && !chat.selected.conversation.isExpired && chat.selected.conversation.status != 'UNASSIGNED') {
          ObjectUtils.setPathFromObject(element, 'conversationInfo.actions.reassign.isRender', props.render, true);
        }
        if (!chat.selected.conversation.isFinished && !chat.selected.conversation.isReassigned && !chat.selected.conversation.isExpired) {
          ObjectUtils.setPathFromObject(element, 'conversationInfo.actions.finalize.isRender', props.render, true);
        }
        if (chat.selected.conversation.isExpired) {
          ObjectUtils.setPathFromObject(element, 'conversationInfo.actions.reopen.isRender', props.render, true);
        }
        if (chat.selected.conversation.isExpired) {
          ObjectUtils.setPathFromObject(element, 'conversationInfo.actions.close.isRender', props.render, true);
        }
        
        ObjectUtils.setPathFromObject(element, 'conversation.isRender', props.render, true);
        ObjectUtils.setPathFromObject(element, 'conversation.loading.isRender', props.render, true);
        if (!chat.selected.conversation.isReassigned && !chat.selected.conversation.isFinished) {
          ObjectUtils.setPathFromObject(element, 'conversation.content.isRender', props.render, true);
          if (chat.selected.conversation.allowWrite) {
            ObjectUtils.setPathFromObject(element, 'conversation.addMessage.isRender', props.render, true);
            ObjectUtils.setPathFromObject(element, 'conversation.quicklyReply.isRender', props.render, true);
            ObjectUtils.setPathFromObject(element, 'conversation.attachmentFile.isRender', props.render, true);
            if (chat.selected.conversation.newMessages) {
              ObjectUtils.setPathFromObject(element, 'conversation.notice.isRender', props.render, true);
            }
          }
        }
        if (chat.selected.conversation.isReassigned || chat.selected.conversation.isFinished || chat.selected.conversation.isExpired || chat.selected.conversation.allowWrite === false) {
          ObjectUtils.setPathFromObject(element, 'conversation.notice.isRender', props.render, true);
        }

        ObjectUtils.setPathFromObject(element, 'conversationContactInfo.isRender', props.render, true);
        ObjectUtils.setPathFromObject(element, 'conversationContactInfo.open.isRender', props.render, true);
        ObjectUtils.setPathFromObject(element, 'conversationContactInfo.data.isRender', props.render, true);
        ObjectUtils.setPathFromObject(element, 'conversationContactInfo.actions.isRender', props.render, true);
      }
      return element;
    }, [chat.selected.conversation]);

    const otherProps = ObjectUtils.findDiffKeys(props, Component.element.defaultProps);
    const className = ComponentUtils.classNames(Component.name, props.className);

    return (
      <div 
        id={props.id}
        className={className}
        ref={element.ref}
        style={props.style} {...otherProps}
      >
        {render.conversationInfo.isRender && <ConversationInfo component={{name: Component.name}} render={render.conversationInfo} />}
        <div className={`${Component.name}__body ${component.state.element.conversationContactInfo.open.show ? 'contact-info-open' : ''}`}>
          {render.conversation.isRender && <Conversation component={{name: Component.name}} ref={element.conversation.ref} render={render.conversation}/>}
          {render.conversationContactInfo.isRender && <ConversationContactInfo component={{name: Component.name}} render={render.conversationContactInfo} element={element.conversationContactInfo} state={component.state.element.conversationContactInfo}/>}
        </div>
      </div>
    );
  })
};

Component.element.displayName = Component.name;

Component.element.propTypes = {
  id: PropTypes.string,
  line: PropTypes.object,
  conversation: PropTypes.object
};

Component.element.defaultProps = {
  __TYPE: Component.name,
  id: null,
  style: null,
  className: null,
  render: null,
  line: null,
  conversation: null
};

export default Component.element;